import { api } from './authClient';
import { readCache, writeCache } from '../offline/cache';
import { mergeBoards } from '../offline/merge';

let status = typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'saved';
const listeners = new Set();
const replaced = new Set();
let tail = Promise.resolve();

function emit(next) {
  status = next;
  listeners.forEach((listener) => listener(status));
}

export function getSyncStatus() {
  return status;
}

export function subscribeSync(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function onBoardReconciled(listener) {
  replaced.add(listener);
  return () => replaced.delete(listener);
}

function boardFromState(state) {
  const { undo, ...rest } = state;
  return rest;
}

function sameBoard(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

async function push(userId) {
  let cached = await readCache(userId);
  if (!cached?.dirty) return 'saved';

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const result = await api('/api/board', {
      method: 'PUT',
      body: JSON.stringify({ ...cached.document, baseRevision: cached.baseRevision || '' }),
    });
    if (result.ok) {
      const document = attempt === 0 ? cached.document : result.body.board || cached.document;
      await writeCache(userId, {
        document,
        baseDocument: document,
        baseRevision: result.body.updatedAt || null,
        dirty: false,
      });
      if (attempt > 0) replaced.forEach((listener) => listener(document));
      return 'saved';
    }
    if (result.status === 409 && result.body?.board) {
      const merged = mergeBoards(cached.baseDocument, result.body.board, cached.document);
      cached = {
        document: merged,
        baseDocument: result.body.board,
        baseRevision: result.body.updatedAt || null,
        dirty: true,
      };
      await writeCache(userId, cached);
      continue;
    }
    if (result.status === 0 || result.body?.error === 'network') return 'offline';
    return 'error';
  }
  return 'error';
}

async function persist(userId, document) {
  const cached = await readCache(userId);
  if (cached && !cached.dirty && sameBoard(cached.document, document)) {
    emit(typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'saved');
    return;
  }
  await writeCache(userId, {
    document,
    baseDocument: cached?.dirty ? cached.baseDocument : cached?.document || document,
    baseRevision: cached?.baseRevision || null,
    dirty: true,
  });
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    emit('offline');
    return;
  }
  emit('syncing');
  const next = await push(userId);
  emit(next === 'saved' ? 'saved' : next);
}

export function saveBoard(state, userId) {
  if (!userId) return;
  const document = boardFromState(state);
  tail = tail.then(() => persist(userId, document)).catch(() => emit('offline'));
}

export async function loadBoard(userId) {
  const cached = await readCache(userId);
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    if (cached?.document) {
      emit('offline');
      return { board: cached.document, offline: true };
    }
    return { error: 'network' };
  }

  const result = await api('/api/board');
  if (!result.ok) {
    if (cached?.document) {
      emit(cached.dirty ? 'offline' : 'saved');
      return { board: cached.document, offline: true };
    }
    return { error: result.body?.error || 'network' };
  }

  const serverBoard = result.body.board || null;
  const updatedAt = result.body.updatedAt || null;
  if (cached?.dirty && cached.document) {
    const merged =
      !updatedAt || updatedAt === cached.baseRevision
        ? cached.document
        : mergeBoards(cached.baseDocument, serverBoard, cached.document);
    await writeCache(userId, {
      document: merged,
      baseDocument: cached.baseDocument || serverBoard,
      baseRevision: cached.baseRevision,
      dirty: true,
    });
    const outcome = await push(userId);
    emit(outcome === 'saved' ? 'saved' : outcome);
    const fresh = await readCache(userId);
    return { board: fresh?.document || merged, offline: outcome !== 'saved' };
  }

  await writeCache(userId, {
    document: serverBoard,
    baseDocument: serverBoard,
    baseRevision: updatedAt,
    dirty: false,
  });
  emit('saved');
  return { board: serverBoard };
}

if (typeof window !== 'undefined') {
  window.addEventListener('offline', () => emit('offline'));
}

export function clearLocalBoard() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) keys.push(localStorage.key(i));
    keys.forEach((key) => {
      if (key === 'teuxdeux.board.v1' || (key && key.startsWith('teuxdeux.board.v1.'))) {
        localStorage.removeItem(key);
      }
    });
    localStorage.removeItem('teuxdeux.activeUser');
    localStorage.removeItem('teuxdeux.legacyClaimed');
  } catch {
    /* ignore */
  }
}
